import React, { useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Grid, Card, CardContent, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from 'recharts';
import { useMonthlyClimateProjections } from '../../api/climateProjections';
import { TrendingUp as TrendingUpIcon, Whatshot as FireIcon, WbSunny as SunIcon } from '@mui/icons-material';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { toDisplayTemperature, toDisplaySpeed, UnitSystem } from '../../utils/units';

const convertValueForMetric = (value: number | null, metric: string, unitSystem: UnitSystem): number | null => {
  if (value == null) return null;
  if (metric === 'temp') return toDisplayTemperature(value, unitSystem);
  if (metric === 'wind') return toDisplaySpeed(value, unitSystem);
  return value;
};

type MetricType = 'fdi' | 'temp' | 'wind' | 'rh' | 'df';

const getMetricConfig = (unitSystem: UnitSystem): Record<MetricType, { label: string; unit: string; color: string; useLogScale: boolean }> => ({
  fdi: { label: 'Fire Danger Index (FFDI)', unit: '', color: '#ff9800', useLogScale: true },
  temp: { label: 'Temperature', unit: unitSystem === 'imperial' ? '°F' : '°C', color: '#f44336', useLogScale: false },
  wind: { label: 'Wind Speed', unit: unitSystem === 'imperial' ? 'mph' : 'km/h', color: '#2196f3', useLogScale: false },
  rh: { label: 'Relative Humidity', unit: '%', color: '#4caf50', useLogScale: false },
  df: { label: 'Drought Factor', unit: '', color: '#ff5722', useLogScale: false },
});

interface ClimateProjectionsViewProps {
  projectId: string;
}

interface WrappedYAxisLabelProps {
  value?: string;
  viewBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
}

const WrappedYAxisLabel: React.FC<WrappedYAxisLabelProps> = ({ value, viewBox }) => {
  if (!value || !viewBox) return null;

  const words = value.split(' ');
  const midpoint = Math.ceil(words.length / 2);
  const lines = words.length > 2
    ? [words.slice(0, midpoint).join(' '), words.slice(midpoint).join(' ')]
    : [value];
  const centerX = (viewBox.x ?? 0) + 18;
  const centerY = (viewBox.y ?? 0) + (viewBox.height ?? 0) / 2;
  const lineHeight = 16;

  return (
    <text
      x={centerX}
      y={centerY}
      fill="rgba(255, 255, 255, 0.7)"
      textAnchor="middle"
      transform={`rotate(-90, ${centerX}, ${centerY})`}
    >
      {lines.map((line, index) => (
        <tspan
          key={line}
          x={centerX}
          dy={index === 0 ? `${-((lines.length - 1) * lineHeight) / 2}px` : `${lineHeight}px`}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

interface ClimateProjectionTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    dataKey?: string;
    name?: string;
    value?: number | string;
  }>;
  unit: string;
}

const ClimateProjectionTooltip: React.FC<ClimateProjectionTooltipProps> = ({ active, label, payload, unit }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 8,
        padding: '12px 14px',
      }}
    >
      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
        {label}
      </div>
      {payload.map((entry) => {
        if (entry.value == null) return null;

        const rawValue = typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value;
        const itemLabel = entry.name ?? entry.dataKey ?? 'value';

        return (
          <div
            key={`${itemLabel}-${rawValue}`}
            style={{
              color: 'rgba(255, 255, 255, 0.88)',
              fontSize: 12,
              marginTop: 6,
            }}
          >
            {itemLabel} : {rawValue}{unit}
          </div>
        );
      })}
    </div>
  );
};

const ClimateProjectionsView: React.FC<ClimateProjectionsViewProps> = ({ projectId }) => {
  const { data: monthlyProjections, isLoading } = useMonthlyClimateProjections(projectId);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('fdi');
  const unitSystem = useUnitSystem();
  const METRIC_CONFIG = useMemo(() => getMetricConfig(unitSystem), [unitSystem]);
  const selectedMetricTitle = METRIC_CONFIG[selectedMetric].label.replace(' (FFDI)', '');
  const selectedMetricTitleWithUnit = METRIC_CONFIG[selectedMetric].unit
    ? `${selectedMetricTitle} (${METRIC_CONFIG[selectedMetric].unit})`
    : selectedMetricTitle;

  const now = new Date();
  const nextMonth = { year: now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear(), month: (now.getMonth() + 2) };

  const allProjections = useMemo(() => {
    if (!monthlyProjections || monthlyProjections.length === 0) return [];
    return monthlyProjections.map((proj: any) => ({
      label: `${proj.year}-${String(proj.month).padStart(2, '0')}`,
      year: proj.year,
      month: proj.month,
      // FDI
      fdi_mean: proj.ensemble_mean,
      fdi_min: proj.ensemble_min,
      fdi_max: proj.ensemble_max,
      // Temperature
      temp_mean: proj.temp_mean,
      temp_min: proj.temp_min,
      temp_max: proj.temp_max,
      // Wind
      wind_mean: proj.wind_mean,
      wind_min: proj.wind_min,
      wind_max: proj.wind_max,
      // RH
      rh_mean: proj.rh_mean,
      rh_min: proj.rh_min,
      rh_max: proj.rh_max,
      // Drought Factor
      df_mean: proj.df_mean,
      df_min: proj.df_min,
      df_max: proj.df_max,
    }));
  }, [monthlyProjections]);

  const chartData = useMemo(() => {
    const meanKey = `${selectedMetric}_mean` as keyof typeof allProjections[0];
    const minKey = `${selectedMetric}_min` as keyof typeof allProjections[0];
    const maxKey = `${selectedMetric}_max` as keyof typeof allProjections[0];
    
    return allProjections
      .filter(d => d.year > nextMonth.year || (d.year === nextMonth.year && d.month >= nextMonth.month))
      .filter(d => d[meanKey] != null && (METRIC_CONFIG[selectedMetric].useLogScale ? d[meanKey] > 0 : true))
      .map(d => ({
        ...d,
        mean: convertValueForMetric(d[meanKey] as number, selectedMetric, unitSystem),
        min: convertValueForMetric(d[minKey] as number, selectedMetric, unitSystem),
        max: convertValueForMetric(d[maxKey] as number, selectedMetric, unitSystem),
      }));
  }, [allProjections, nextMonth.year, nextMonth.month, selectedMetric, unitSystem, METRIC_CONFIG]);

  const trendStats = useMemo(() => {
    if (allProjections.length === 0) return null;

    const meanKey = `${selectedMetric}_mean` as keyof typeof allProjections[0];
    const nearTermEndYear = now.getFullYear() + 5;
    const nearTerm = allProjections.filter(
      d => (d.year > nextMonth.year || (d.year === nextMonth.year && d.month >= nextMonth.month)) && d.year <= nearTermEndYear
    );
    const farTerm = allProjections.filter(d => d.year >= 2045 && d.year <= 2050);

    if (nearTerm.length === 0 || farTerm.length === 0) return null;

    const nearMeanRaw = nearTerm.reduce((sum, d) => sum + ((d[meanKey] as number) ?? 0), 0) / nearTerm.length;
    const farMeanRaw = farTerm.reduce((sum, d) => sum + ((d[meanKey] as number) ?? 0), 0) / farTerm.length;
    const increase = nearMeanRaw !== 0 ? ((farMeanRaw - nearMeanRaw) / nearMeanRaw) * 100 : 0;

    const nearMean = convertValueForMetric(nearMeanRaw, selectedMetric, unitSystem) ?? 0;
    const farMean = convertValueForMetric(farMeanRaw, selectedMetric, unitSystem) ?? 0;

    return {
      nearTerm: nearMean.toFixed(1),
      farTerm: farMean.toFixed(1),
      nearTermLabel: `${now.getFullYear()}–${nearTermEndYear}`,
      increase: increase.toFixed(1),
    };
  }, [allProjections, selectedMetric, unitSystem]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#ff6b35' }} />
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body1" color="text.secondary">
          Climate projection data is not yet available for this project.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {trendStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={4}>
            <Card sx={{ bgcolor: 'rgba(100, 181, 246, 0.1)', border: '1px solid rgba(100, 181, 246, 0.3)', height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SunIcon sx={{ color: '#64b5f6' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                    Near-Term {selectedMetricTitleWithUnit}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                  {trendStats.nearTerm}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {trendStats.nearTermLabel} Average
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4}>
            <Card sx={{ bgcolor: 'rgba(255, 107, 53, 0.1)', border: '1px solid rgba(255, 107, 53, 0.3)', height: '100%' }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FireIcon sx={{ color: '#ff6b35' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                    Long-Term {selectedMetricTitleWithUnit}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                  {trendStats.farTerm}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  2045–2050 Average
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={4}>
            <Card sx={{ 
              bgcolor: parseFloat(trendStats.increase) > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)', 
              border: parseFloat(trendStats.increase) > 0 ? '1px solid rgba(244, 67, 54, 0.3)' : '1px solid rgba(76, 175, 80, 0.3)', 
              height: '100%' 
            }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingUpIcon sx={{ color: parseFloat(trendStats.increase) > 0 ? '#f44336' : '#4caf50' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase' }}>
                    Projected Change
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 600 }}>
                  {parseFloat(trendStats.increase) > 0 ? '+' : ''}{trendStats.increase}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {trendStats.nearTermLabel} → 2045–2050
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
            Climate Change Projections (2026-2050)
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Metric</InputLabel>
              <Select
                value={selectedMetric}
                label="Metric"
                onChange={(e) => setSelectedMetric(e.target.value as MetricType)}
                sx={{
                  color: '#fff',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '.MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              >
                <MenuItem value="fdi">Fire Danger Index</MenuItem>
                <MenuItem value="temp">Temperature</MenuItem>
                <MenuItem value="wind">Wind Speed</MenuItem>
                <MenuItem value="rh">Relative Humidity</MenuItem>
                <MenuItem value="df">Drought Factor</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: METRIC_CONFIG[selectedMetric].color, borderRadius: '50%' }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Ensemble Mean</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: `${METRIC_CONFIG[selectedMetric].color}50` }} />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Model Range</Typography>
            </Box>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="label" 
              angle={-45}
              textAnchor="end"
              height={80}
              interval={Math.floor(chartData.length / 20)}
              tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
              stroke="rgba(255, 255, 255, 0.5)"
            />
            <YAxis 
              scale={METRIC_CONFIG[selectedMetric].useLogScale ? 'log' : 'auto'}
              domain={METRIC_CONFIG[selectedMetric].useLogScale ? [1, 'auto'] : ['auto', 'auto']}
              width={80}
              label={{ 
                value: `${METRIC_CONFIG[selectedMetric].label}${METRIC_CONFIG[selectedMetric].unit ? ` (${METRIC_CONFIG[selectedMetric].unit})` : ''}`,
                position: 'insideLeft',
                content: <WrappedYAxisLabel />
              }}
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
              stroke="rgba(255, 255, 255, 0.5)"
              allowDataOverflow
            />
            <Tooltip content={<ClimateProjectionTooltip unit={METRIC_CONFIG[selectedMetric].unit} />} />
            
            {/* Model range as shaded band */}
            <Area
              type="monotone"
              dataKey="max"
              stroke="none"
              fill={`${METRIC_CONFIG[selectedMetric].color}33`}
              fillOpacity={1}
            />
            <Area
              type="monotone"
              dataKey="min"
              stroke="none"
              fill="rgba(42, 42, 46, 1)"
              fillOpacity={1}
            />
            
            {/* Danger threshold reference lines - only for FDI */}
            {selectedMetric === 'fdi' && (
              <>
                <ReferenceLine y={12} stroke="#ffeb3b" strokeWidth={2} strokeDasharray="5 5" />
                <ReferenceLine y={25} stroke="#ffa726" strokeWidth={2} strokeDasharray="5 5" />
                <ReferenceLine y={50} stroke="#f44336" strokeWidth={2} strokeDasharray="5 5" />
                <ReferenceLine y={100} stroke="#b71c1c" strokeWidth={2} strokeDasharray="5 5" />
              </>
            )}
            
            {/* Ensemble mean line */}
            <Line 
              type="monotone" 
              dataKey="mean" 
              stroke={METRIC_CONFIG[selectedMetric].color} 
              strokeWidth={3}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 2, display: 'block' }}>
          Monthly {METRIC_CONFIG[selectedMetric].label} projections based on ensemble of 7 climate models.
          Shaded area represents the range across all models.
          {selectedMetric === 'fdi' && ' Dashed lines indicate fire danger thresholds.'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ClimateProjectionsView;
